import { jsPDF } from 'jspdf';
import 'svg2pdf.js';
import { api } from '../api/client';
import { DateFormatPattern, Logo, PdfExportSettings, Task, WeekNumbering } from '../api/types';
import { computeDepthById, diffDaysUTC, parseISODate, addDaysUTC } from './ganttGeometry';
import { computePagePlan, orientedPaperSizeMm, PAGE_HEADER_BAND_MM, PAGE_HEADER_GAP_MM, PX_TO_MM, LOGO_SIZE_MM } from './pdfLayout';
import { planDateRange } from './planDateRange';
import { buildLogoSvgMarkup } from './pdfLogoSvg';
import { rasterizeLogoToPngDataUri } from './rasterizeLogo';
import { buildPageContentSvg, computeGlobalArrows, computeGlobalLayout } from './pdfPageSvg';
import { escapeXml } from './svgText';

/**
 * Génère et télécharge le PDF d'export d'un diagramme, entièrement côté
 * client, en PDF VECTORIEL (jsPDF + svg2pdf.js — https://github.com/yWorks/svg2pdf.js,
 * licence MIT) : le tableau, la frise, les connecteurs et les logos sont de
 * vraies formes/texte PDF (nets à tout zoom, fichier léger), pas une image
 * bitmap. Pas de dépendance lourde côté serveur (Puppeteer/Chromium), ce qui
 * convient mieux à un déploiement Docker autohébergé.
 *
 * Pagination : voir utils/pdfLayout.ts (computePagePlan, source UNIQUE
 * partagée avec l'aperçu de la modale de réglages) et utils/pdfPageSvg.ts
 * (construction du SVG de chaque page à partir de cette même géométrie).
 * Le bandeau nom+logos, en haut de CHAQUE page, a une taille FIXE en mm
 * (PAGE_HEADER_BAND_MM) indépendante de `scalePercent` : réduire l'échelle
 * du contenu ne doit jamais rendre les logos illisibles.
 */

async function renderSvgString(doc: jsPDF, markup: string, opts: { x: number; y: number; width: number; height: number }): Promise<void> {
  const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');
  const el = parsed.documentElement;
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-99999px';
  container.style.top = '0';
  container.appendChild(el);
  document.body.appendChild(container);
  try {
    await doc.svg(el as unknown as HTMLElement, opts);
  } finally {
    document.body.removeChild(container);
  }
}

function sanitizeFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'diagramme';
}

export interface GenerateDiagramPdfParams {
  diagramId: string;
  diagramName: string;
  settings: Required<PdfExportSettings>;
  dateFormat: DateFormatPattern;
  weekNumbering: WeekNumbering;
  locale: string;
  weekAbbrev: (n: number) => string;
  columnLabel: (col: string) => string;
}

export async function generateDiagramPdf({
  diagramId,
  diagramName,
  settings,
  dateFormat,
  weekNumbering,
  locale,
  weekAbbrev,
  columnLabel,
}: GenerateDiagramPdfParams): Promise<void> {
  const [allTasks, allLogos] = await Promise.all([api.get<Task[]>(`/diagrams/${diagramId}/tasks`), api.get<Logo[]>('/logos')]);

  const rowFiltered = settings.onlyVisibleRows ? allTasks.filter((task) => task.visible) : allTasks;

  const defaultRange = planDateRange(rowFiltered);
  const startIso = settings.dateRangeStart ? settings.dateRangeStart.slice(0, 10) : defaultRange?.start;
  const endIso = settings.dateRangeEnd ? settings.dateRangeEnd.slice(0, 10) : defaultRange?.end;
  const rangeStart = startIso ? parseISODate(startIso) : new Date();
  const rangeEnd = endIso ? parseISODate(endIso) : addDaysUTC(rangeStart, 29);
  const totalDays = Math.max(1, diffDaysUTC(rangeStart, rangeEnd) + 1);

  const rangeEndIso = rangeEnd.toISOString().slice(0, 10);
  const rangeStartIso = rangeStart.toISOString().slice(0, 10);
  const tasks = rowFiltered.filter((task) => {
    if (!task.startDate || !task.endDate) return true;
    return task.startDate.slice(0, 10) <= rangeEndIso && task.endDate.slice(0, 10) >= rangeStartIso;
  });

  const today = new Date();
  const todayUtc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const todayOffset = diffDaysUTC(rangeStart, todayUtc);
  const todayDayIndex = todayOffset >= 0 && todayOffset < totalDays ? todayOffset : null;

  const depthById = computeDepthById(tasks);
  const layout = computeGlobalLayout(tasks, rangeStart);
  const { arrows, pathFor } = computeGlobalArrows(layout, settings.connectorStyle);

  const plan = computePagePlan({
    taskCount: tasks.length,
    dayCount: totalDays,
    columns: settings.columns,
    paperSize: settings.paperSize,
    orientation: settings.orientation,
    marginCm: settings.marginCm,
    scalePercent: settings.scalePercent,
  });

  const paper = orientedPaperSizeMm(settings.paperSize, settings.orientation);
  // jsPDF swaps width/height back to match its DEFAULT ('p') orientation when it isn't told
  // otherwise, even with an explicit [w,h] format array — pass it explicitly or a "landscape"
  // paper silently comes out portrait (and truncated, since the content was laid out for the
  // wider page).
  const doc = new jsPDF({ unit: 'mm', format: [paper.w, paper.h], orientation: settings.orientation });
  const scale = settings.scalePercent / 100;

  const logosById = new Map(allLogos.map((logo) => [logo.id, logo]));
  const logos = settings.logoIds.map((id) => logosById.get(id)).filter((l): l is Logo => !!l);

  const contentY0 = plan.marginMm + PAGE_HEADER_BAND_MM + PAGE_HEADER_GAP_MM;

  let firstPage = true;
  for (let pageRow = 0; pageRow < plan.pagesTall; pageRow++) {
    const rowStart = pageRow * plan.rowsPerPage;
    const rowCount = Math.min(plan.rowsPerPage, tasks.length - rowStart);
    if (rowCount <= 0) continue;

    for (let pageCol = 0; pageCol < plan.pagesWide; pageCol++) {
      if (!firstPage) doc.addPage([paper.w, paper.h], settings.orientation);
      firstPage = false;

      const includeLabelCol = pageCol === 0;
      const dayStart = pageCol === 0 ? 0 : plan.daysOnFirstCol + (pageCol - 1) * plan.daysPerLaterCol;
      const dayCapacity = pageCol === 0 ? plan.daysOnFirstCol : plan.daysPerLaterCol;
      const dayCount = Math.max(0, Math.min(dayCapacity, totalDays - dayStart));

      // Bandeau nom + logos, taille fixe, répété sur chaque page.
      const headerSvg =
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${plan.usableWmm} ${PAGE_HEADER_BAND_MM}" width="${plan.usableWmm}" height="${PAGE_HEADER_BAND_MM}">` +
        `<text x="1" y="${PAGE_HEADER_BAND_MM / 2 + 2}" font-size="5" font-family="Helvetica, Arial, sans-serif" font-weight="bold" fill="#0f172a">${escapeXml(diagramName)}</text>` +
        `<line x1="0" y1="${PAGE_HEADER_BAND_MM - 0.5}" x2="${plan.usableWmm}" y2="${PAGE_HEADER_BAND_MM - 0.5}" stroke="#0f172a" stroke-width="0.5"/>` +
        `</svg>`;
      await renderSvgString(doc, headerSvg, { x: plan.marginMm, y: plan.marginMm, width: plan.usableWmm, height: PAGE_HEADER_BAND_MM });

      if (logos.length > 0) {
        const gapMm = 3;
        const totalLogosWidth = logos.length * LOGO_SIZE_MM + (logos.length - 1) * gapMm;
        const startX = plan.marginMm + (plan.usableWmm - totalLogosWidth) / 2;
        const logoY = plan.marginMm + (PAGE_HEADER_BAND_MM - LOGO_SIZE_MM) / 2;
        for (let i = 0; i < logos.length; i++) {
          const logo = logos[i];
          const x = startX + i * (LOGO_SIZE_MM + gapMm);
          const vectorMarkup = buildLogoSvgMarkup(logo.svgContent, logo.cropX, logo.cropY, logo.zoom, 100, `logo${i}_`);
          let drew = false;
          if (vectorMarkup) {
            try {
              await renderSvgString(doc, vectorMarkup, { x, y: logoY, width: LOGO_SIZE_MM, height: LOGO_SIZE_MM });
              drew = true;
            } catch {
              drew = false;
            }
          }
          if (!drew) {
            try {
              const png = await rasterizeLogoToPngDataUri(logo.svgContent, logo.cropX, logo.cropY, logo.zoom, 96);
              doc.addImage(png, 'PNG', x, logoY, LOGO_SIZE_MM, LOGO_SIZE_MM);
            } catch {
              // Logo ni vectorisable ni rastérisable (SVG corrompu) : on continue sans lui plutôt que d'interrompre tout l'export.
            }
          }
        }
      }

      if (dayCount <= 0 && !includeLabelCol) continue;

      const { svg, widthPx, heightPx } = buildPageContentSvg({
        tasks,
        depthById,
        layout,
        arrows,
        pathFor,
        includeLabelCol,
        columns: settings.columns,
        labelColWidth: plan.labelColWidth,
        dateFormat,
        weekNumbering,
        rangeStart,
        dayStart,
        dayCount,
        rowStart,
        rowCount,
        todayDayIndex,
        weekAbbrev,
        columnLabel,
        locale,
      });

      const widthMm = widthPx * PX_TO_MM * scale;
      const heightMm = heightPx * PX_TO_MM * scale;
      await renderSvgString(doc, svg, { x: plan.marginMm, y: contentY0, width: widthMm, height: heightMm });
    }
  }

  doc.save(`${sanitizeFilename(diagramName)}.pdf`);
}
